// frontend/src/pages/Cartorios.js
import React, { useEffect, useState } from "react";
import "./Complemento.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function Cartorios() {
  const [cartorios, setCartorios] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoCartorio, setNovoCartorio] = useState("");
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [cartoriosPorPagina, setCartoriosPorPagina] = useState(5);
  const [loading, setLoading] = useState(true);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("cartoriosGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("cartoriosGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const perfilUsuario = localStorage.getItem("perfil");

  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);

    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  useEffect(() => {
    const tk = localStorage.getItem("token");

    fetch("/api/cartorios", {
      headers: {
        Authorization: `Bearer ${tk}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.cartorios) ? data.cartorios : [];
        setCartorios(lista);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar cartórios:", err);
        setLoading(false);
      });
  }, []);

  const handleCadastrar = (e) => {
    e.preventDefault();

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar cartórios.");
      return;
    }

    const tk = localStorage.getItem("token");
    const nomeFormatado = novoCartorio.trim().toLowerCase();

    const jaExiste = cartorios.some((c) => c.nome.trim().toLowerCase() === nomeFormatado);
    if (jaExiste) {
      alert("Esse cartório já está cadastrado");
      return;
    }

    if (!novoCartorio.trim()) {
      alert("Preencha o campo Cartório.");
      return;
    }

    fetch("/api/cartorios", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: novoCartorio }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.id && data.nome) {
          setCartorios((prev) => [
            {
              ...data,
              nome: (data.nome ?? novoCartorio).toUpperCase(),
            },
            ...prev,
          ]);

          setPaginaAtual(1);
        }

        setNovoCartorio("");
      })
      .catch((err) => console.error("Erro ao cadastrar cartório:", err));
  };

  const handleSaveEdit = (id) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar cartórios.");
      return;
    }

    const tk = localStorage.getItem("token");

    fetch(`/api/cartorios/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: nomeEditado }),
    })
      .then(() => {
        setCartorios((prev) => prev.map((c) => (c.id === id ? { ...c, nome: nomeEditado } : c)));
        setEditandoId(null);
        setNomeEditado("");
      })
      .catch((err) => console.error("Erro ao editar cartório:", err));
  };

  const handleEdit = (cartorio) => {
    setEditandoId(cartorio.id);
    setNomeEditado(cartorio.nome);
  };

  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir cartórios.");
      return;
    }

    const confirmar = window.confirm("Deseja realmente excluir este cartório?");
    if (!confirmar) return;

    const tk = localStorage.getItem("token");

    fetch(`/api/cartorios/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(() => {
        setCartorios((prev) => prev.filter((c) => c.id !== id));
      })
      .catch((err) => console.error("Erro ao excluir cartório:", err));
  };

  const cartoriosFiltrados = cartorios.filter((c) => c.nome.toLowerCase().includes(filtro.toLowerCase()));

  const indexInicial = (paginaAtual - 1) * cartoriosPorPagina;
  const indexFinal = indexInicial + cartoriosPorPagina;
  const cartoriosPaginados = cartoriosFiltrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(cartoriosFiltrados.length / cartoriosPorPagina) || 1;

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Cartório"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const linhas = (cartoriosFiltrados || []).map((c) => [c.nome ?? ""].map(csvEscape));

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "cartorios.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="complemento-container">
        <h2>Cartórios</h2>

        <div className="bloco-linha">
          <TextField
            label="Filtrar cartório por nome"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "var(--ig-t-9ca3af)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = cartoriosFiltrados.map((c) => c.nome).join(", ");
                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }
                const ok = await copiarTexto(texto);
                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando cartórios...</p>
            ) : cartoriosFiltrados.length === 0 ? (
              <p className="erro">Nenhum cartório cadastrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "82%" }}>Cartório</th>
                      <th style={{ width: "18%", textAlign: "center" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartoriosPaginados.map((cartorio) => (
                      <tr key={cartorio.id}>
                        <td className="nome-local">
                          {editandoId === cartorio.id ? (
                            <TextField size="small" value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value)} fullWidth />
                          ) : (
                            cartorio.nome
                          )}
                        </td>
                        <td className="botoes-acao" style={{ textAlign: "center", verticalAlign: "middle" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {editandoId === cartorio.id ? (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<i className="fas fa-save" />}
                                onClick={() => handleSaveEdit(cartorio.id)}
                                sx={{
                                  minWidth: 110,
                                  height: 38,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  background: "#2e7d32",
                                  "&:hover": {
                                    background: "#1b5e20",
                                  },
                                }}
                              >
                                Salvar
                              </Button>
                            ) : (
                              <>
                                {podeEditar && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<i className="fas fa-pen-to-square" />}
                                    onClick={() => handleEdit(cartorio)}
                                    sx={{
                                      minWidth: 110,
                                      height: 38,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      background: "#1998a8",
                                      "&:hover": {
                                        background: "#177f8d",
                                      },
                                    }}
                                  >
                                    Editar
                                  </Button>
                                )}

                                {podeExcluir && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<i className="fas fa-trash" />}
                                    onClick={() => handleDelete(cartorio.id)}
                                    sx={{
                                      minWidth: 110,
                                      height: 38,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      background: "#d32f2f",
                                      "&:hover": {
                                        background: "#b71c1c",
                                      },
                                    }}
                                  >
                                    Excluir
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={cartoriosFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(e, newPage) => setPaginaAtual(newPage + 1)}
                  rowsPerPage={cartoriosPorPagina}
                  rowsPerPageOptions={[5, 10, 25]}
                  onRowsPerPageChange={(e) => {
                    setCartoriosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  labelRowsPerPage="Cartórios por página:"
                  sx={{
                    color: "var(--ig-t-ffffff)",
                    "& .MuiTablePagination-toolbar": {
                      paddingLeft: 0,
                      paddingRight: 0,
                      minHeight: 58,
                    },
                    "& .MuiTablePagination-selectLabel": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiIconButton-root": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiSelect-select": {
                      color: "var(--ig-t-ffffff)",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {podeCadastrar && (
          <div className="bloco-formulario">
            <Box
              component="form"
              onSubmit={handleCadastrar}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                id="cartorio"
                label="Cartório"
                value={novoCartorio}
                onChange={(e) => setNovoCartorio(e.target.value.toUpperCase())}
                placeholder="Digite o nome do Cartório"
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16.5px 14px",
                  },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, 16px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<i className="fas fa-save" />}
                  sx={{
                    width: 240,
                    minWidth: 240,
                    maxWidth: 240,

                    height: 52,
                    minHeight: 52,

                    backgroundColor: "#2e7d32",

                    color: "#fff",

                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 700,

                    borderRadius: "6px",

                    "&:hover": {
                      backgroundColor: "#1b5e20",
                    },
                  }}
                >
                  Cadastrar
                </Button>
              </Box>
            </Box>
          </div>
        )}

        {perfilUsuario === "Visitante" && (
          <p style={{ textAlign: "center", color: "var(--ig-t-aaaaaa)", marginTop: "1rem" }}>🔒 Você possui acesso apenas de visualização.</p>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Cartorios;
